import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAdminHubDetail,
  getAdminHubEvents,
  getAdminHubPartners,
} from "../../services/ingroove/adminService";
import { formatCurrency, formatDate, formatDateTime } from "../../utils/formatters";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Wallet,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Ticket,
} from "lucide-react";

const TX_PAGE_SIZE = 10;

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const roleBadgeVariant = (role) => {
  switch (role?.toLowerCase()) {
    case "admin": return "danger";
    case "operations": return "info";
    case "finance": return "warning";
    case "content": return "success";
    default: return "default";
  }
};

const txBadgeVariant = (description) => {
  switch (description) {
    case "TicketSale": return "success";
    case "Refund": return "info";
    case "Payout": return "warning";
    case "PaymentGatewayFee": return "default";
    case "Commission": return "warning";
    default: return "default";
  }
};

const HubDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [hub, setHub] = useState(null);
  const [events, setEvents] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wallet transaction filter/pagination
  const [txSearch, setTxSearch] = useState("");
  const [txTypeFilter, setTxTypeFilter] = useState("");
  const [txPage, setTxPage] = useState(1);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [hubRes, eventsRes, partnersRes] = await Promise.all([
        getAdminHubDetail(id),
        getAdminHubEvents(id),
        getAdminHubPartners({ hubId: id, pageSize: 100 }),
      ]);
      setHub(hubRes);
      setEvents(eventsRes.events ?? []);
      setPartners(partnersRes.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    setTxPage(1);
  }, [txSearch, txTypeFilter]);

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-6 bg-bg-surface rounded w-32" />
        <div className="h-48 bg-bg-surface border border-border rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-40 bg-bg-surface border border-border rounded-2xl" />
            <div className="h-64 bg-bg-surface border border-border rounded-2xl" />
            <div className="h-64 bg-bg-surface border border-border rounded-2xl" />
          </div>
          <div className="space-y-4">
            <div className="h-40 bg-bg-surface border border-border rounded-2xl" />
            <div className="h-64 bg-bg-surface border border-border rounded-2xl" />
          </div>
        </div>
      </div>
    );

  if (!hub)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Building2 size={32} className="text-border-hover mb-3" />
        <p className="text-sm text-text-muted">Hub not found.</p>
      </div>
    );

  const wallet = hub.wallet;
  const transactions = wallet?.transactions ?? [];

  // Filtered + paginated transactions
  const filteredTx = transactions.filter((tx) => {
    const matchesSearch =
      !txSearch ||
      tx.name?.toLowerCase().includes(txSearch.toLowerCase()) ||
      tx.reference?.toLowerCase().includes(txSearch.toLowerCase());

    const matchesType =
      txTypeFilter === ""
        ? true
        : txTypeFilter === "Credit"
        ? tx.transactionType === "Credit"
        : tx.transactionType === "Debit";

    return matchesSearch && matchesType;
  });

  const txTotalPages = Math.ceil(filteredTx.length / TX_PAGE_SIZE);
  const pagedTx = filteredTx.slice(
    (txPage - 1) * TX_PAGE_SIZE,
    txPage * TX_PAGE_SIZE
  );

  const walletStats = [
    {
      label: "Balance",
      value: formatCurrency(wallet?.balance ?? 0),
      icon: <Wallet size={14} className="text-primary" />,
      accent: "bg-primary/10",
    },
    {
      label: "Total Credits",
      value: formatCurrency(wallet?.totalTopUps ?? 0),
      icon: <TrendingUp size={14} className="text-green-400" />,
      accent: "bg-green-400/10",
    },
    {
      label: "Total Debits",
      value: formatCurrency(wallet?.totalSpent ?? 0),
      icon: <TrendingDown size={14} className="text-red-400" />,
      accent: "bg-red-400/10",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/ingroove/hubs")}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
      >
        <ArrowLeft size={13} /> Back to Hubs
      </button>

      {/* Hero banner */}
      <div className="relative h-48 bg-bg-elevated rounded-2xl overflow-hidden">
        {hub.image ? (
          <img
            src={hub.image}
            alt={hub.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 size={40} className="text-border-hover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{hub.name}</h2>
            {hub.address && (
              <div className="flex items-center gap-1.5 text-white/60 text-xs mt-0.5">
                <MapPin size={11} />
                {hub.address}
              </div>
            )}
          </div>
          <Badge
            label={hub.isActive ? "Active" : "Inactive"}
            variant={hub.isActive ? "success" : "danger"}
          />
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left column (2/3) ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Hub info */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5 space-y-5">
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">Hub Info</h3>
            </div>

            {/* Bio */}
            {hub.bio && (
              <p className="text-sm text-text-secondary leading-relaxed">{hub.bio}</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-1">
              {/* Owner */}
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Owner</p>
                <div className="flex items-center gap-3">
                  <Avatar name={hub.ownerName?.split(" ")[0]} surname={hub.ownerName?.split(" ")[1]} image={hub.ownerImage} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary font-medium truncate">{hub.ownerName}</p>
                    <p className="text-xs text-text-muted truncate">{hub.ownerEmail}</p>
                  </div>
                </div>
              </div>

              {/* Banking */}
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Banking</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CreditCard size={12} className="text-text-muted flex-shrink-0" />
                    <span className="text-sm text-text-primary">
                      {hub.bankName ?? <span className="text-text-hint">No bank set</span>}
                    </span>
                  </div>
                  {hub.branchCode && (
                    <p className="text-xs text-text-muted pl-5">Branch: {hub.branchCode}</p>
                  )}
                  {hub.maskedAccountNumber && (
                    <p className="text-xs text-text-muted pl-5">Account: {hub.maskedAccountNumber}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Meta row */}
            <div className="flex flex-wrap gap-4 pt-2 border-t border-border text-xs text-text-muted">
              <span>Created {formatDate(hub.createdAt)}</span>
              <span>Address {hub.showAddress ? "visible" : "hidden"}</span>
              <code className="text-text-hint">{hub.id?.slice(0, 8)}...</code>
            </div>
          </div>

          {/* Events list */}
          <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
              <Calendar size={14} className="text-purple-400" />
              <h3 className="text-sm font-semibold text-text-primary">Events</h3>
              <span className="ml-auto text-xs text-text-muted">{events.length} total</span>
            </div>

            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Calendar size={24} className="text-border-hover mb-2" />
                <p className="text-xs text-text-muted">No events yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {events.map((event) => {
                  const status = statusConfig[event.status] ?? statusConfig[0];
                  const ticketProgress =
                    event.tickets?.totalCapacity > 0
                      ? Math.min(
                          (event.tickets.totalSold / event.tickets.totalCapacity) * 100,
                          100
                        )
                      : 0;

                  return (
                    <div
                      key={event.eventId}
                      className="px-5 py-4 hover:bg-bg-elevated transition"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="text-sm text-text-primary font-medium truncate">
                            {event.title}
                          </p>
                          <p className="text-xs text-text-muted mt-0.5">
                            {formatDate(event.date)}
                          </p>
                        </div>
                        <Badge label={status.label} variant={status.variant} />
                      </div>

                      {/* Ticket progress */}
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-text-muted mb-1">
                            <span className="flex items-center gap-1">
                              <Ticket size={10} />
                              {event.tickets?.totalSold ?? 0} / {event.tickets?.totalCapacity ?? 0} tickets
                            </span>
                            <span>{ticketProgress.toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-bg-elevated rounded-full h-1">
                            <div
                              className="h-1 rounded-full bg-primary transition-all"
                              style={{ width: `${ticketProgress}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Revenue row */}
                      <div className="flex gap-4 text-xs">
                        <span className="text-text-muted">
                          Tickets{" "}
                          <span className="text-text-primary font-medium">
                            {formatCurrency(event.tickets?.earnedRevenue ?? 0)}
                          </span>
                        </span>
                        <span className="text-text-muted">
                          Extras{" "}
                          <span className="text-text-primary font-medium">
                            {formatCurrency(event.extras?.earnedRevenue ?? 0)}
                          </span>
                        </span>
                        <span className="text-text-muted ml-auto">
                          Total{" "}
                          <span className="text-primary font-semibold">
                            {formatCurrency(
                              (event.tickets?.earnedRevenue ?? 0) +
                              (event.extras?.earnedRevenue ?? 0)
                            )}
                          </span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Wallet transactions */}
          <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Wallet size={14} className="text-primary" />
                <h3 className="text-sm font-semibold text-text-primary">
                  Wallet Transactions
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                  />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                    className="pl-8 pr-8 py-1.5 text-xs bg-bg-base border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition w-44"
                  />
                  {txSearch && (
                    <button
                      onClick={() => setTxSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-muted"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Credit/Debit toggle */}
                <div className="flex gap-1 bg-bg-base border border-border rounded-xl p-1">
                  {["", "Credit", "Debit"].map((f) => (
                    <button
                      key={f}
                      onClick={() => setTxTypeFilter(f)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                        txTypeFilter === f
                          ? "ingroove-gradient-bg text-white shadow-glow"
                          : "text-text-muted hover:text-text-secondary"
                      }`}
                    >
                      {f === "" ? "All" : f}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredTx.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-xs text-text-muted">No transactions found</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {pagedTx.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between px-5 py-3 hover:bg-bg-elevated transition"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          tx.transactionType === "Credit" ? "bg-green-400" : "bg-red-400"
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-text-primary font-medium truncate">
                          {tx.name}
                        </p>
                        {tx.reference && (
                          <code className="text-xs text-text-hint block truncate">
                            {tx.reference}
                          </code>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge
                            label={tx.transactionDescription}
                            variant={txBadgeVariant(tx.transactionDescription)}
                          />
                          <span className="text-xs text-text-hint">
                            {formatDateTime(tx.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p
                        className={`text-sm font-semibold ${
                          tx.transactionType === "Credit" ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {tx.transactionType === "Credit" ? "+" : "-"}
                        {formatCurrency(Math.abs(tx.amount))}
                      </p>
                      <p className="text-xs text-text-hint mt-0.5">
                        Bal: {formatCurrency(tx.balance)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {txTotalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-border text-xs text-text-muted">
                <span>
                  Page {txPage} of {txTotalPages} — {filteredTx.length} transactions
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                    disabled={txPage === 1}
                    className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={13} />
                  </button>
                  <button
                    onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                    disabled={txPage === txTotalPages}
                    className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right column (1/3) ── */}
        <div className="space-y-5">

          {/* Wallet stat cards */}
          <div className="space-y-3">
            {walletStats.map((s) => (
              <div
                key={s.label}
                className="bg-bg-surface border border-border rounded-xl p-4 flex items-center gap-3"
              >
                <div className={`p-2 rounded-lg flex-shrink-0 ${s.accent}`}>{s.icon}</div>
                <div>
                  <p className="text-xs text-text-muted">{s.label}</p>
                  <p className="text-sm font-semibold text-text-primary mt-0.5">{s.value}</p>
                </div>
              </div>
            ))}

            {/* Quick stats */}
            <div className="bg-bg-surface border border-border rounded-xl p-4 grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Calendar size={13} className="text-purple-400" />
                </div>
                <p className="text-sm font-semibold text-text-primary">{hub.totalEvents}</p>
                <p className="text-xs text-text-muted">Events</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Users size={13} className="text-blue-400" />
                </div>
                <p className="text-sm font-semibold text-text-primary">{hub.totalPartners}</p>
                <p className="text-xs text-text-muted">Partners</p>
              </div>
            </div>
          </div>

          {/* Partners list */}
          <div className="bg-bg-surface border border-border rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
              <Users size={14} className="text-blue-400" />
              <h3 className="text-sm font-semibold text-text-primary">Hub Partners</h3>
              <span className="ml-auto text-xs text-text-muted">{partners.length} total</span>
            </div>

            {partners.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <p className="text-xs text-text-muted">No partners yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {partners.map((partner) => (
                  <div
                    key={partner.id}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-bg-elevated transition"
                  >
                    <Avatar
                      name={partner.name}
                      surname={partner.surname}
                      image={partner.image}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-text-primary truncate">
                        {partner.name} {partner.surname}
                      </p>
                      <p className="text-xs text-text-muted truncate">{partner.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge
                        label={partner.role ?? "—"}
                        variant={roleBadgeVariant(partner.role)}
                      />
                      <Badge
                        label={partner.isActive ? "Active" : "Inactive"}
                        variant={partner.isActive ? "success" : "default"}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubDetail;
